import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import {
  SocialPost,
  CreateSocialPostRequest,
  UpdateSocialPostRequest,
  UseSocialPostsResult
} from '@/types/social';
import { toast } from 'sonner';

export function useSocialPosts(businessId: string | null): UseSocialPostsResult {
  const queryClient = useQueryClient();

  const queryKey = ['social-posts', businessId];

  const {
    data: posts = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SocialPost[];
    },
    enabled: !!businessId,
  });

  // Subscribe to realtime updates
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`social-posts-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_posts',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  const createPost = async (request: CreateSocialPostRequest): Promise<SocialPost> => {
    // Determine initial status
    let status: SocialPost['status'] = 'draft';
    if (request.publish_now) {
      status = 'publishing';
    } else if (request.scheduled_for) {
      status = 'scheduled';
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('social_posts')
      .insert({
        business_id: request.business_id,
        content: request.content,
        media_urls: request.media_urls || [],
        media_types: request.media_types || [],
        target_platforms: request.target_platforms,
        platform_overrides: request.platform_overrides || {},
        scheduled_for: request.scheduled_for,
        status,
        created_by: user?.id,
      })
      .select()
      .single();

    if (error) throw error;

    // If publishing now, call the publish function
    if (request.publish_now) {
      try {
        await supabase.functions.invoke('social-publish-post', {
          body: { post_id: data.id }
        });
      } catch (publishError) {
        console.error('Publish error:', publishError);
        // Update status to failed
        await supabase
          .from('social_posts')
          .update({ status: 'failed', error_message: 'Failed to publish' })
          .eq('id', data.id);
      }
    }

    queryClient.invalidateQueries({ queryKey });
    return data as SocialPost;
  };

  const updatePost = async (postId: string, request: UpdateSocialPostRequest): Promise<SocialPost> => {
    const updateData: Record<string, any> = {};

    if (request.content !== undefined) updateData.content = request.content;
    if (request.media_urls !== undefined) updateData.media_urls = request.media_urls;
    if (request.media_types !== undefined) updateData.media_types = request.media_types;
    if (request.target_platforms !== undefined) updateData.target_platforms = request.target_platforms;
    if (request.platform_overrides !== undefined) updateData.platform_overrides = request.platform_overrides;
    if (request.scheduled_for !== undefined) updateData.scheduled_for = request.scheduled_for;
    if (request.status !== undefined) updateData.status = request.status;

    const { data, error } = await supabase
      .from('social_posts')
      .update(updateData)
      .eq('id', postId)
      .select()
      .single();

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey });
    return data as SocialPost;
  };

  const deletePost = async (postId: string): Promise<void> => {
    const { error } = await supabase
      .from('social_posts')
      .delete()
      .eq('id', postId);

    if (error) throw error;

    queryClient.invalidateQueries({ queryKey });
  };

  const publishNow = async (postId: string): Promise<void> => {
    // Update status to publishing
    await supabase
      .from('social_posts')
      .update({ status: 'publishing' })
      .eq('id', postId);

    // Call publish function
    const { error } = await supabase.functions.invoke('social-publish-post', {
      body: { post_id: postId }
    });

    if (error) {
      // Revert status
      await supabase
        .from('social_posts')
        .update({ status: 'scheduled', error_message: error.message })
        .eq('id', postId);
      throw error;
    }

    queryClient.invalidateQueries({ queryKey });
  };

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    posts,
    isLoading,
    error: error as Error | null,
    createPost,
    updatePost,
    deletePost,
    publishNow,
    refresh,
  };
}

export default useSocialPosts;
